import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { MdiIcon, Table, useSnackbar } from '@components';
import { mdiMagnify, mdiSync } from '@mdi/js';
import moment from 'moment';
import { CircularProgress } from '@mui/material';
const OiList = ({ $state, CrmService, user, client }) => {
    const [data, setData] = useState();
    const [loading, setLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const [error, setError] = useState(false);
    const { showSnackbar } = useSnackbar();
    const [filter, setFilter] = useState([]); // filtros a aplicar na tabela
    const [sort, setSort] = useState([]); // ordenação da tabela
    const [syncing, setSyncing] = useState(false);
    const [options, setOptions] = useState({
        filter: [], // exemplo -> [{name: 'nome', type: 'TEXT'}, {name: 'ultimoContacto', type: 'DATE', options: {format: 'YYYY-MM-DD'}}, ...]
        sort: [] // exemplo -> ['id', 'nome', 'ultimoContacto', 'contactoTelefonico', 'nif', 'email', 'morada', 'localidade']
    });
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 5
    });
    const [departamentos, setDepartamentos] = useState([]);
    // Filtragem dentro de cada coluna
    // Modo de filtro (igual a, contém, começa com... )
    const getType = (type) => {
        switch (type) {
            case 'contains':
                return '$ilike';
            case 'startsWith':
                return '$sw';
            case 'notEquals':
                return '$not';
            case 'empty':
                return '$null';
            case 'notEmpty':
                return '$notNull';
            case 'greaterThan':
                return '$gt';
            case 'greaterThanOrEqualTo':
                return '$gte';
            case 'lessThan':
                return '$lt';
            case 'lessThanOrEqualTo':
                return '$lte';
            default:
                return '$eq';
        }
    };
    const columns = useMemo(() => [
        {
            accessorKey: 'numeroOi',
            header: 'Número OI',
            size: 150
        },
        {
            accessorKey: 'data',
            header: 'Data',
            size: 150,
            Cell: ({ row }) => (moment(row.original?.data).isValid() ? moment(row.original?.data).format('DD-MM-YYYY') : '-')
        },
        {
            accessorKey: 'Departamento.id',
            exportKey: 'Departamento.designacao',
            header: 'Área',
            size: 120,
            filterSelectOptions: departamentos.map(r => {
                return {
                    label: r.designacao,
                    value: r.id
                };
            }),
            Cell: ({ row }) => row.original?.Departamento?.designacao
        },
        {
            accessorKey: 'observacoes',
            header: 'Observações',
            size: 200,
            Cell: ({ cell }) => (<div style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '250px' // Defina conforme necessário
                }} title={cell.getValue()} // Mostra o texto completo ao passar o rato
            >
            {cell.getValue()}
          </div>)
        }
    ], [departamentos]);
    const initialFetch = async () => {
        if (loading) {
            /* const id = await CrmService.getUserId();
            setUserId(id); */
            const dep = await CrmService.getDepartamentos();
            setDepartamentos(dep);
            // opt -> vai buscar os campos filtraveis
            const opt = await CrmService.optionsOi();
            setOptions(opt);
            setLoading(false);
        }
    };
    const fetchData = async () => {
        setError(false);
        try {
            let filters = {};
            filter.forEach(r => {
                filters[r.id] = `${getType(r.type)}:${r.value}`;
            });
            let sorter = [];
            sort.forEach(r => {
                sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
            });
            if (client !== undefined) {
                filters['coreClienteId'] = `$eq:${client}`;
            }
            const aux = await CrmService.findAllOi({
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                filter: filters,
                sortBy: sorter
            });
            // Update URL
            $state.go($state.current.name, {
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                sortBy: sorter,
                filter: Object.keys(filter).length > 0 ? JSON.stringify(filters) : undefined
            }, {
                notify: false,
                reload: false,
                location: 'replace',
                inherit: true
            });
            setData(aux);
        }
        catch (e) {
            console.log(e);
            setError(true);
        }
        setRefetching(false);
    };
    // Table
    useEffect(() => {
        initialFetch();
        if (!refetching) {
            setRefetching(true);
            fetchData();
        }
    }, [pagination, filter, sort]);
    return (<Box>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography variant='h6' sx={{ color: 'black' }}>
          OI - SGI
        </Typography>
        {client && (<Tooltip title={'Sincronizar OIs e processos'} placement='top'>
            <IconButton color='primary' disabled={syncing} onClick={async () => {
                try {
                    setSyncing(true);
                    const res = await CrmService.syncClienteOis(client);
                    showSnackbar(`Sincronização efetuada com sucesso: Foram adicionadas ${res.oisCriadas} OIs e ${res.processosCriados} processos`);
                    window.dispatchEvent(new Event('syncCliente'));
                    fetchData();
                }
                catch (err) {
                    console.error(err);
                    alert('Erro ao sincronizar');
                }
                finally {
                    setSyncing(false);
                }
            }}>
              {syncing ? <CircularProgress size={20}/> : <MdiIcon path={mdiSync} sx={{ color: '#1976D2' }}/>}
            </IconButton>
          </Tooltip>)}
      </Stack>

      <Box sx={{
            mt: 1,
            borderRadius: '15px',
            border: '1px solid #e0e0e0',
            overflow: 'hidden',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
        }}>
        <Table storageKey={$state.current.name} data={data} columns={columns} initial={{
            sort: sort,
            filter: filter
        }} isLoading={refetching} options={options} onPaginate={setPagination} onFilter={setFilter} onSort={setSort} rowActionSize={150} actionItems={({ row }) => [
            <Box key={`actions-${row.original.id}`} gap={1} sx={{
                    width: '100%', // Ocupa toda a largura da célula
                    height: '100%', // Ocupa toda a altura da célula
                    display: 'flex',
                    justifyContent: 'center', // Centra horizontalmente
                    alignItems: 'center' // Centra verticalmente
                }}>
              <Tooltip title={'Ver detalhes'} placement='top'>
                <IconButton color='secondary' onClick={() => {
                    //$state.go('app.susene.processes.details', row.original.id);
                    switch (row.original.coreDepartamentoId) {
                        case 3: {
                            // LME
                            $state.go('app.lab.interventions.detail', { id: row.original.oiId });
                            break;
                        }
                        case 4: {
                            // ELEVADORES
                            $state.go('app.elv.oi.details', { id: row.original.oiId });
                            break;
                        }
                        case 7: {
                            // SUS_ENE
                            $state.go('app.susene.interventions.detail', { id: row.original.oiId });
                            break;
                        }
                    }
                }}>
                  <MdiIcon path={mdiMagnify} sx={{ color: '#1976D2' }}/>
                </IconButton>
              </Tooltip>
            </Box>
        ]}></Table>
      </Box>
    </Box>);
};
export default OiList;
